import { useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { TreatmentPlanState } from "../typings/enums";
import { useSideEffectConsultQuery } from "./react-query/useConsultQuery";
import { useLabsRequiredQuery } from "./react-query/useLabsRequiredQuery";
import { useLatestPrescriptionRealtime } from "./react-query/useLatestPrescriptionRealtime";
import { useMessagesRealtime } from "./react-query/useMessagesRealTime";
import {
  useLatestRefillSessionByUser,
  useRefillSession,
} from "./react-query/useRefillSession";
import { MessageRecipient } from "../utils/messageUtils";
import { toJSDate } from "../utils/toJSDate";

type ReturnType = {
  data?: TreatmentPlanState;
  isLoading: boolean;
};

export const useTreatmentPlanState = (): ReturnType => {
  const { currentUser } = useAuth();
  const { data: sideEffectConsult, isLoading: sideEffectConsultLoading } =
    useSideEffectConsultQuery(currentUser?.uid || "");
  const { data: labsRequired, isLoading: labsRequiredLoading } =
    useLabsRequiredQuery(currentUser?.uid || "");
  const {
    data: latestIncompleteRefillSession,
    isLoading: latestIncompleteRefillSessionLoading,
  } = useLatestRefillSessionByUser(currentUser?.uid, false);
  const {
    data: latestCompleteRefillSession,
    isLoading: latestCompleteRefillSessionLoading,
  } = useLatestRefillSessionByUser(currentUser?.uid, true);
  const prescriptionData = useLatestPrescriptionRealtime();
  const {
    data: refillSessionOnPrescription,
    isLoading: refillSessionOnPrescriptionLoading,
  } = useRefillSession(
    prescriptionData?.refillSessionId ?? "",
    currentUser?.uid ?? ""
  );
  const { data: messages, isLoading: messagesLoading } = useMessagesRealtime(
    currentUser?.uid ?? ""
  );

  const lastMessageFromClinician = useMemo(() => {
    if (!messages || messages.length === 0) return false;

    const refillSubmissionDate = toJSDate(
      latestCompleteRefillSession?.updatedAt
    );

    const refillStartedDate = toJSDate(
      latestIncompleteRefillSession?.createdAt
    );

    const mostRecentRefillActivity =
      refillSubmissionDate && refillStartedDate
        ? new Date(
            Math.max(
              refillSubmissionDate.getTime(),
              refillStartedDate.getTime()
            )
          )
        : refillSubmissionDate || refillStartedDate || null;

    const sideEffectConsultSubmissionDate = sideEffectConsult?.createdAt
      ? new Date(sideEffectConsult.createdAt)
      : null;

    const lastMessageAt = new Date(messages[0]?.sentAt).getTime();

    const refillSubmittedAfterMessage =
      mostRecentRefillActivity &&
      mostRecentRefillActivity.getTime() > lastMessageAt;

    const sideEffectConsultSubmittedAfterMessage =
      sideEffectConsultSubmissionDate &&
      sideEffectConsultSubmissionDate.getTime() > lastMessageAt;

    const waitingForResponse =
      refillSubmittedAfterMessage || sideEffectConsultSubmittedAfterMessage;

    // 0 element of the array is the last message (sorted by sentAt desc)
    return (
      messages[0].recipient === MessageRecipient.Patient && !waitingForResponse
    );
  }, [
    messages,
    latestCompleteRefillSession?.updatedAt,
    latestIncompleteRefillSession?.createdAt,
    sideEffectConsult?.createdAt,
  ]);

  const isLoading =
    sideEffectConsultLoading ||
    labsRequiredLoading ||
    latestIncompleteRefillSessionLoading ||
    latestCompleteRefillSessionLoading ||
    refillSessionOnPrescriptionLoading ||
    messagesLoading;

  if (isLoading) return { isLoading };

  // Default to normal treatment plan state
  let state: TreatmentPlanState = TreatmentPlanState.TreatmentPlan;

  // Check if the refill session on the prescription has been started (has answers)
  const prescriptionRefillHasAnswers =
    Object.keys(refillSessionOnPrescription?.answers ?? {})?.length > 0;

  if (labsRequired) {
    state = TreatmentPlanState.LabsRequired;
  } else if (
    // If prescription has a refill session that hasn't been started and refill date has passed
    prescriptionData?.refillSessionId &&
    !prescriptionRefillHasAnswers &&
    new Date(prescriptionData?.refillDate as string).getTime() <
      new Date().getTime()
  ) {
    state = TreatmentPlanState.RefillCheckIn;
  } else if (latestIncompleteRefillSession) {
    // If there's any incomplete session, show continue (unless it was caught by RefillCheckIn above)
    state = TreatmentPlanState.ContinueCheckIn;
  } else if (
    latestCompleteRefillSession &&
    prescriptionData?.refillDate &&
    latestCompleteRefillSession.updatedAt &&
    (toJSDate(latestCompleteRefillSession.updatedAt)?.getTime() ?? 0) >
      new Date(prescriptionData?.refillDate).getTime()
  ) {
    state = TreatmentPlanState.CheckInReview;
  } else if (
    sideEffectConsult &&
    ["completed", "rejected", "referred_out"].indexOf(
      sideEffectConsult.visitStatus
    ) === -1
  ) {
    state = TreatmentPlanState.SideEffects;
  } else if (lastMessageFromClinician) {
    // moved down so refill/side-effect states win first
    state = TreatmentPlanState.MessageFromProvider;
  }

  return { data: state, isLoading };
};
